// ============================================================================
// Componente: RHForms.js
// Objetivo: Central de formulários dentro do Módulo de RH
// Padrão visual: RH / Vermelho. Renderiza os formulários inline (embedded).
// Escopo: RH puro — Entrevista de Desligamento, Primeiros 15 Dias,
//         Pesquisa de Experiência do Colaborador.
// ============================================================================

import React, { useState } from 'react';
import {
    UserMinus,
    CalendarHeart,
    CalendarClock,
    Smile,
    FileEdit,
    Search,
    LineChart,
    Archive,
    ArrowLeft,
    CheckCircle2
} from 'lucide-react';

import EntrevistaDesligamentoQuestionario from './RHFormsEntrevistaDesligamento_Questionario';
import EntrevistaDesligamentoRelatorio from './RHFormsEntrevistaDesligamento_Relatorio';
import EntrevistaDesligamentoHistorico from './RHFormsEntrevistaDesligamento_Historico';
import Primeiro15Consulta from './RHFormsPrimeiro15_Consulta';
import Primeiro15Relatorio from './RHFormsPrimeiro15_Relatorio';
import PesquisaExperienciaConsulta from './RHFormsPesquisaExperienciaColaborador_Consulta';
import PesquisaExperienciaRelatorio from './RHFormsPesquisaExperienciaColaborador_Relatorio';
import PesquisaEscalaConsulta from '../pesquisa-escala/RHFormsPesquisaEscala_Consulta';
import PesquisaEscalaRelatorio from '../pesquisa-escala/RHFormsPesquisaEscala_Relatorio';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

const COR = '#e11d48';
const COR_ESCURA = '#be123c';
const COR_FUNDO = '#fff1f2';

const RHForms = ({ subInicial }) => {
    // subInicial (ex.: 'p15-consulta') vem do deep-link da Home. Se for uma sub-aba
    // inexistente ou sem permissão, o render cai no fallback (visão geral) sem quebrar.
    const [activeSubTab, setActiveSubTab] = useState(subInicial || 'visao-geral');

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const role = perfilNoModulo(user?.perfil, 'rh') || '';

    const isAdmin = role === 'Admin';
    const isRH = role === 'RH';
    const isDiretoria = role === 'Diretoria';
    const isGerente = role === 'Gerente';
    const isEntrevistaDesligamento = role === 'EntrevistaDesligamento';
    const isOperacional = role === 'Operacional';

    // Permissões finas (espelham Forms.js, restritas ao escopo do módulo RH)
    const canEntrevistaQuest = isAdmin || isDiretoria || isEntrevistaDesligamento || isRH;
    // Relatório (com os feedbacks) liberado ao Gerente em 2026-07-17 — vê só a
    // própria loja (recorte por loja no backend) e sem o cargo nos feedbacks.
    const canEntrevistaRelat = isRH || isAdmin || isDiretoria || isGerente || isOperacional;
    // Histórico (análise do formato antigo) segue restrito — o Gerente só entra no Relatório.
    const canEntrevistaHistorico = isRH || isAdmin || isDiretoria || isOperacional;

    // "Preencher" interno do 15 dias foi removido (2026-07-08): o colaborador
    // responde pelo link externo; o RH só consulta/relatório.
    const canP15Consulta = isRH || isAdmin || isDiretoria || isGerente || isOperacional;
    const canP15Relat = isRH || isAdmin || isDiretoria || isGerente || isOperacional; // Gerente incluído em 2026-07-17

    const canPCConsulta = isRH || isAdmin || isDiretoria;
    const canPCRelat = isRH || isAdmin || isDiretoria;

    // Pesquisa de Escala — mesmos perfis do backend (PERFIL_PESQUISA em
    // server/rh/server-pesquisa-escala.js). ⚠️ A tela de acompanhamento mostra
    // nome e celular de quem não recebeu; o Gerente não entra aqui.
    const canEscalaConsulta = isRH || isAdmin || isDiretoria;
    const canEscalaRelat = isRH || isAdmin || isDiretoria;

    // Definição dos formulários e suas views (id da sub-aba → componente embedded)
    const FORMS = [
        {
            id: 'entrevista',
            title: 'Entrevista de Desligamento',
            desc: 'Questionário padrão para colaboradores em processo de rescisão.',
            icon: <UserMinus size={28} />,
            views: [
                { key: 'entrevista-quest', tipo: 'preencher', show: canEntrevistaQuest, Componente: EntrevistaDesligamentoQuestionario },
                { key: 'entrevista-relat', tipo: 'relatorio', show: canEntrevistaRelat, Componente: EntrevistaDesligamentoRelatorio },
                { key: 'entrevista-historico', tipo: 'historico', show: canEntrevistaHistorico, Componente: EntrevistaDesligamentoHistorico }
            ]
        },
        {
            id: 'primeiro15',
            title: 'Primeiros 15 Dias',
            desc: 'Avaliação de integração e adaptação inicial do novo colaborador.',
            icon: <CalendarHeart size={28} />,
            views: [
                { key: 'p15-consulta', tipo: 'consultar', show: canP15Consulta, Componente: Primeiro15Consulta },
                { key: 'p15-relat', tipo: 'relatorio', show: canP15Relat, Componente: Primeiro15Relatorio }
            ]
        },
        {
            id: 'pesquisa-experiencia',
            title: 'Pesquisa de Experiência do Colaborador',
            desc: 'Mapeia fatores psicossociais do trabalho para orientar ações preventivas e registrar evidências na gestão de riscos do PGR.',
            icon: <Smile size={28} />,
            views: [
                { key: 'pc-consulta', tipo: 'consultar', show: canPCConsulta, Componente: PesquisaExperienciaConsulta },
                { key: 'pc-relat', tipo: 'relatorio', show: canPCRelat, Componente: PesquisaExperienciaRelatorio }
            ]
        },
        {
            id: 'pesquisa-escala',
            title: 'Pesquisa de Escala',
            desc: 'Ouve a operação sobre a jornada de trabalho — 12x36 ou 5x2: a escala de hoje, a preferida e o que combina com a vida fora do trabalho.',
            icon: <CalendarClock size={28} />,
            views: [
                { key: 'esc-consulta', tipo: 'consultar', show: canEscalaConsulta, Componente: PesquisaEscalaConsulta },
                { key: 'esc-relat', tipo: 'relatorio', show: canEscalaRelat, Componente: PesquisaEscalaRelatorio }
            ]
        }
    ];

    // Mapa plano de sub-aba → componente, para renderização
    const viewPorChave = {};
    FORMS.forEach((f) => f.views.forEach((v) => { viewPorChave[v.key] = v; }));

    const botaoTipo = {
        preencher: { label: 'Preencher', icon: <FileEdit size={16} /> },
        consultar: { label: 'Consultar', icon: <Search size={16} /> },
        relatorio: { label: 'Relatórios', icon: <LineChart size={16} /> },
        historico: { label: 'Histórico', icon: <Archive size={16} /> }
    };

    const FormCard = ({ form }) => {
        const visiveis = form.views.filter((v) => v.show);
        if (visiveis.length === 0) return null;

        return (
            <div
                style={{
                    backgroundColor: '#fff', borderRadius: '16px', padding: '24px',
                    border: '1px solid #f0e2e2', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                    display: 'flex', flexDirection: 'column', height: '100%', transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)'; }}
                onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05)'; }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                    <div style={{ backgroundColor: `${COR}15`, color: COR, padding: '16px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {form.icon}
                    </div>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0 }}>{form.title}</h3>
                </div>
                <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.5', flex: 1, marginBottom: '24px' }}>
                    {form.desc}
                </p>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {visiveis.map((v) => {
                        const t = botaoTipo[v.tipo];
                        const primario = v.tipo === 'preencher';
                        const secundario = v.tipo === 'consultar';
                        const style = primario
                            ? { backgroundColor: COR, color: '#fff', border: 'none' }
                            : secundario
                                ? { backgroundColor: '#f8fafc', color: COR, border: `1px solid ${COR}` }
                                : { backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' };
                        return (
                            <button
                                key={v.key}
                                onClick={() => setActiveSubTab(v.key)}
                                style={{
                                    flex: 1, minWidth: '100px', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', gap: '8px', padding: '10px', borderRadius: '8px',
                                    fontWeight: '600', fontSize: '13px', cursor: 'pointer', transition: 'opacity 0.2s',
                                    ...style
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                                onMouseOut={(e) => { e.currentTarget.style.opacity = '1'; }}
                            >
                                {t.icon} {t.label}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    const VisaoGeral = () => (
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
            gap: '24px'
        }}>
            {FORMS.map((f) => <FormCard key={f.id} form={f} />)}
        </div>
    );

    // Sub-aba ativa só é válida se existir e o usuário tiver permissão
    const viewAtiva = activeSubTab !== 'visao-geral' ? viewPorChave[activeSubTab] : null;
    const subViewValida = viewAtiva && viewAtiva.show;

    if (subViewValida) {
        const Componente = viewAtiva.Componente;
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <button
                    onClick={() => setActiveSubTab('visao-geral')}
                    style={{
                        alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '8px 16px', borderRadius: '8px', border: `1px solid ${COR_FUNDO}`,
                        backgroundColor: COR_FUNDO, color: COR_ESCURA, fontSize: '14px', fontWeight: '700',
                        cursor: 'pointer'
                    }}
                >
                    <ArrowLeft size={16} /> Voltar aos formulários
                </button>
                <Componente embedded />
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{
                display: 'flex', flexWrap: 'wrap', gap: '12px',
                borderBottom: '1px solid #f0e2e2', paddingBottom: '16px'
            }}>
                <button style={{
                    display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px',
                    backgroundColor: COR_FUNDO, color: COR, border: `1px solid ${COR}`,
                    borderRadius: '8px', fontWeight: '700', fontSize: '14px', cursor: 'default'
                }}>
                    <CheckCircle2 size={18} /> Visão Geral
                </button>
            </div>

            <VisaoGeral />
        </div>
    );
};

export default RHForms;
